// EbayPriceValue.js — displays market prices for any collection type
// PriceCharting for games/consoles/lego, eBay for movies/music/books

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Link
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  LocalOffer as PriceTagIcon,
  TrendingUp as PriceIcon
} from '@mui/icons-material';
import axiosInstance from '../axiosInstance';

// Module-level cache — survives component unmount/remount within the same browser session.
// Key: "type:name:platform:format:setNum", Value: response data
const clientCache = new Map();

const makeCacheKey = (type, name, platform, format, setNum) =>
  `${type}:${(name || '').toLowerCase()}:${platform || ''}:${format || ''}:${setNum ? setNum.split('-')[0] : ''}`;

/**
 * Displays market prices for a collection item.
 * PriceCharting (GBP, PAL-aware) for games/consoles/lego.
 * eBay UK listings for movies/music/books.
 *
 * @param {string}   props.type       - 'game' | 'console' | 'lego' | 'movie' | 'music' | 'book'
 * @param {string}   props.name       - Item name
 * @param {string}   [props.platform] - Platform (games)
 * @param {string}   [props.format]   - Format (movies, music)
 * @param {string}   [props.setNum]   - Set number (lego)
 */
const EbayPriceValue = ({ type, name, platform, format, setNum }) => {
  const cacheKey = makeCacheKey(type, name, platform, format, setNum);
  const [loading, setLoading] = useState(false);
  const [priceData, setPriceData] = useState(() => clientCache.get(cacheKey) || null);
  const [error, setError] = useState(null);

  const fetchPrice = async () => {
    if (!name || !type) return;

    // Check client cache first — avoids the loading flash on revisit
    const hit = clientCache.get(cacheKey);
    if (hit) { setPriceData(hit); return; }

    setLoading(true);
    setError(null);

    try {
      const params = { name, type };
      if (platform) params.platform = platform;
      if (format) params.format = format;
      if (setNum) params.setNum = setNum.split('-')[0];

      const response = await axiosInstance.get('/ebay-prices/value', { params, timeout: 15000 });

      if (response.data.found) {
        clientCache.set(cacheKey, response.data);
        setPriceData(response.data);
      } else {
        setPriceData(null);
        setError(response.data.message || 'No price data found');
      }
    } catch (err) {
      setPriceData(null);
      setError('Failed to fetch price data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!name || !type) return;
    // Already have data from client cache — no fetch needed
    if (clientCache.get(cacheKey)) return;
    // Stagger requests so multiple visible items don't all fire at once
    const stagger = Array.from(name).reduce((acc, ch) => acc + ch.charCodeAt(0), 0) % 2000;
    const timer = setTimeout(fetchPrice, stagger);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, type, platform, format, setNum]);

  const fmt = (val) => `${priceData?.currencySymbol || '£'}${parseFloat(val).toFixed(2)}`;

  const isPriceCharting = priceData?.source === 'PriceCharting';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
        <CircularProgress size={20} sx={{ mr: 1 }} />
        <Typography variant="body2">Checking market prices…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">{error}</Typography>
        <Button startIcon={<RefreshIcon />} size="small" onClick={fetchPrice} sx={{ mt: 1 }}>
          Try Again
        </Button>
      </Box>
    );
  }

  if (!priceData) {
    return (
      <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
        <Button startIcon={<PriceTagIcon />} size="small" variant="outlined" onClick={fetchPrice}>
          Check market prices
        </Button>
      </Box>
    );
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <PriceIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Market Prices</Typography>
        </Box>

        {isPriceCharting ? (
          /* PriceCharting: used/complete breakdown */
          <Box sx={{ mb: 1 }}>
            {priceData.usedPrice && (
              <Typography variant="body1">
                <strong>{type === 'console' ? 'Console only' : type === 'lego' ? 'Used' : 'Loose'}:</strong>{' '}
                {fmt(priceData.usedPrice)}
              </Typography>
            )}
            {priceData.completePrice && (
              <Typography variant="body2" color="text.secondary">
                <strong>{type === 'console' ? 'Boxed' : 'Complete (CIB)'}:</strong>{' '}
                {fmt(priceData.completePrice)}
              </Typography>
            )}
          </Box>
        ) : (
          /* eBay: median + range */
          <Box sx={{ mb: 1 }}>
            <Typography variant="body1">
              <strong>Median:</strong> {fmt(priceData.sellPrice)}
            </Typography>
            {priceData.minPrice && priceData.maxPrice && (
              <Typography variant="body2" color="text.secondary">
                Range: {fmt(priceData.minPrice)} – {fmt(priceData.maxPrice)}
                {priceData.count > 1 && ` (${priceData.count} listings)`}
              </Typography>
            )}
          </Box>
        )}

        {priceData.sampleListings?.length > 0 && priceData.sampleListings[0]?.url && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" display="block">
              <Link href={priceData.sampleListings[0].url} target="_blank" rel="noopener noreferrer">
                {priceData.sampleListings[0].title?.length > 70
                  ? `${priceData.sampleListings[0].title.slice(0, 70)}…`
                  : priceData.sampleListings[0].title}
              </Link>
            </Typography>
          </>
        )}

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Source: {priceData.source}
          </Typography>
          <Button startIcon={<RefreshIcon />} size="small" onClick={() => {
            clientCache.delete(cacheKey);
            setPriceData(null);
            fetchPrice();
          }}>
            Refresh
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EbayPriceValue;
